import { useCallback, useEffect, useState } from "react";
import { Container, Content, Title } from "./style";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { Search as SearchIcon, Visibility } from "@mui/icons-material";
import { Error } from "../Error";
import { Header } from "../Header";
import { apiOperation, headers } from "../../services/api";
import { Loading } from "../Loading";
import { getSessionUser } from "../../utils/users";
import { IUser } from "../Login/ILogin";
import {
  RelacionamentoCliente,
  RelacionamentoClienteImportacao,
} from "../../models/relacionamentoCliente";
import { Paginacao, Resultado } from "../../models/base/basic";
import useDebouncedState from "../../utils/useDebouncedState";

interface PaginationState {
  paginaCorrente: number;
  quantidadePaginas?: number;
  tamanhoPagina: number;
  quantidadeRegistros: number;
}

interface ErrorState {
  message: string;
  title: string;
  state: boolean;
}

const INITIAL_PAGINATION: PaginationState = {
  paginaCorrente: 0,
  quantidadePaginas: 0,
  tamanhoPagina: 10,
  quantidadeRegistros: 0,
};

export const HistoryImport = () => {
  const [historyList, setHistoryList] = useState<RelacionamentoClienteImportacao[]>([]);
  const [showLoading, setShowLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const [errorApi, setErrorApi] = useState<ErrorState>({
    message: "",
    title: "",
    state: false,
  });

  const [modalDetails, setModalDetails] = useState<RelacionamentoCliente[]>([]);
  const { value: searchTerm, setValue: setSearchTerm, debouncedValue: debouncedSearchTerm } = useDebouncedState("", 1000);
  const [modalPagination, setModalPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const [isModalLoading, setIsModalLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<RelacionamentoClienteImportacao | null>(null);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({
      ...prev,
      paginaCorrente: 0,
      tamanhoPagina: parseInt(event.target.value, 10),
    }));
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPagination(prev => ({
      ...prev,
      paginaCorrente: newPage,
    }));
  }, []);

  const handleChangeModalRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setModalPagination(prev => ({
      ...prev,
      paginaCorrente: 0,
      tamanhoPagina: parseInt(event.target.value, 10),
    }));
  }, []);

  const handleChangeModalPage = useCallback((event: unknown, newPage: number) => {
    setModalPagination(prev => ({
      ...prev,
      paginaCorrente: newPage,
    }));
  }, []);

    const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedHistory(null);
    setModalDetails([]);
    setSearchTerm("");
    setModalPagination(INITIAL_PAGINATION);
  }, [setSearchTerm]);

  const fetchModalDetails = useCallback(async (
    importacaoId: string,
    pagina: number = 0,
    registrosPorPagina: number = 10,
    busca: string | null = null
  ) => {
    try {
      setIsModalLoading(true);
      const urlString = `/relacionamento-cliente/cliente/historico/${importacaoId}?pagina=${pagina}&registrosPorPagina=${registrosPorPagina}${busca ? `&busca=${busca}` : ""}`;

      const { data } = await apiOperation.get<Resultado<Paginacao<RelacionamentoCliente>>>(
        urlString,
        { headers: headers() }
      );

      setModalPagination(data.valor);
      setModalDetails(data.valor.dados);
    } catch (error: any) {
      setErrorApi({
        title: `Erro ao buscar detalhes`,
        message: `Não foi possível carregar os detalhes da importação. Erro: ${error?.response?.status} - ${error?.response?.statusText}`,
        state: true,
      });
      handleCloseModal();
    } finally {
      setIsModalLoading(false);
    }
  }, [handleCloseModal]);

  const handleOpenModal = useCallback(async (item: RelacionamentoClienteImportacao) => {
    setSelectedHistory(item);
    setIsModalOpen(true);
    setModalPagination(INITIAL_PAGINATION);
    await fetchModalDetails(item.relacionamentoClienteImportacaoId.toString(), 0, 10);
  }, [fetchModalDetails]);


  const fetchHistory = useCallback(async () => {
    try {
      setShowLoading(true);
      const urlString = `/relacionamento-cliente/cliente/importacao?pagina=${pagination.paginaCorrente}&registrosPorPagina=${pagination.tamanhoPagina}`;

      const { data } = await apiOperation.get<RelacionamentoClienteImportacao[]>(
        urlString,
        { headers: headers() }
      );

      setHistoryList(data);
    } catch (error: any) {
      setErrorApi({
        title: `API Não disponivel`,
        message: `Caso o problema continue informe o seguinte erro: ${error?.response?.status}-${error?.response?.statusText}`,
        state: true,
      });
    } finally {
      setShowLoading(false);
    }
  }, [pagination.paginaCorrente, pagination.tamanhoPagina]);

useEffect(() => {
  if (isModalOpen && selectedHistory) {
    const pageToUse = debouncedSearchTerm !== searchTerm ? 0 : modalPagination.paginaCorrente;
    
    fetchModalDetails(
      selectedHistory.relacionamentoClienteImportacaoId.toString(),
      pageToUse,
      modalPagination.tamanhoPagina,
      debouncedSearchTerm || null
    );

    if (pageToUse === 0 && modalPagination.paginaCorrente !== 0) {
      setModalPagination(prev => ({ ...prev, paginaCorrente: 0 }));
    }
  }
}, [
  isModalOpen,
  selectedHistory,
  modalPagination.paginaCorrente,
  modalPagination.tamanhoPagina,
  debouncedSearchTerm,
  searchTerm, 
  fetchModalDetails
]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return (
    <>
      <Header />
      <Container>
        <Content>
          <Title>Histórico Classificação Relacionamento Cliente</Title>
        </Content>
        <Card sx={{ minWidth: 275 }}>
          {showLoading ? (
            <Loading hidden={!showLoading} />
          ) : (
            <CardContent>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Solicitante</TableCell>
                      <TableCell>Data da Consulta</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Visualizar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyList.map((item) => (
                      <TableRow
                        key={item.relacionamentoClienteImportacaoId}
                        hover
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell>{item.usuarioInclusao}</TableCell>
                        <TableCell>
                          {moment(item.dataInclusao).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell align="center">
                          <div
                            style={{
                              backgroundColor: item.status === "Concluído" ? "var(--success)" : "var(--info)",
                              borderRadius: "15px",
                              width: "fit-content",
                              padding: "inherit",
                              color: "var(--text-on-inverse)",
                              margin: "auto",
                            }}
                          >
                            {item.status}
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleOpenModal(item)}
                            color="primary"
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={pagination.quantidadeRegistros}
                  page={pagination.paginaCorrente}
                  onPageChange={handleChangePage}
                  rowsPerPage={pagination.tamanhoPagina}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Registros Por Página: "
                />
              </TableContainer>
            </CardContent>
          )}
        </Card>

        <Error
          hasOpen={errorApi.state}
          handleClose={() => setErrorApi(prev => ({ ...prev, state: false }))}
          errorMsg={errorApi.message}
          title={errorApi.title}
        />

        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
            }}>
              <Typography variant="h6">Detalhes da Importação</Typography>
              <TextField
                size="small"
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                sx={{ minWidth: "300px" }}
              />
            </div>
          </DialogTitle>
          <DialogContent dividers>
            {isModalLoading ? (
              <Loading hidden={false} />
            ) : (
              <>
                <TableContainer sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>CNPJ/CPF</TableCell>
                        <TableCell>Nome do Cliente</TableCell>
                        <TableCell align="right">Valor Total Títulos</TableCell>
                        <TableCell align="right">Valor Total Limite</TableCell>
                        <TableCell align="right">Valor Total Vencido</TableCell>
                        <TableCell align="right">Valor Total a Vencer</TableCell>
                        <TableCell align="right">Média Atraso</TableCell>
                        <TableCell align="right">Atraso Máximo</TableCell>
                        <TableCell align="right">Prazo Médio Pagamento</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalDetails.map((cliente) => (
                        <TableRow key={cliente.relacionamentoClienteId} hover>
                          <TableCell>{cliente.cnpjouCpf}</TableCell>
                          <TableCell>{cliente.nomeCliente}</TableCell>
                          <TableCell align="right">
                            {cliente.valorTotalTitulos.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                          <TableCell align="right">
                            {cliente.valorTotalLimite.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: cliente.valorTotalVencido > 0 ? "var(--error-text)" : "inherit",
                            }}
                          >
                            {cliente.valorTotalVencido.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                          <TableCell align="right">
                            {cliente.valorTotalVencer.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </TableCell>
                          <TableCell align="right">{cliente.mediaAtrasoDias}</TableCell>
                          <TableCell align="right">{cliente.atrasoMaximoDias}</TableCell>
                          <TableCell align="right">{cliente.prazoMedioPagamento}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={modalPagination.quantidadeRegistros}
                  page={modalPagination.paginaCorrente}
                  onPageChange={handleChangeModalPage}
                  rowsPerPage={modalPagination.tamanhoPagina}
                  onRowsPerPageChange={handleChangeModalRowsPerPage}
                  labelRowsPerPage="Registros Por Página: "
                  sx={{ borderTop: 1, borderColor: "divider", mt: 2 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};